import { useEffect, useRef, useState } from "react";
import { useCustomerAnalytics } from "../../../services/hooks/useCustomerAnalytics";
import { useCartMutations } from "../../../services/hooks/useCartMutations";
import { CartItemType } from "../../../services/redux/types/CartLoyaltyV2Types";
import { cartItemTypeToAddLineInput } from "../../../utils/cart/cartLineTypes";
import {
  CUSTOMER_ANALYTICS_EVENT,
  ChangeCounterFrom,
  EditCartItemEnum,
} from "../../../utils/enums";
import { localizedNumber } from "../../../utils/help/localization/localization";
import { AddIcon, MinusIcon } from "../Icons/Icons";
import styles from "./Counter.module.scss";

export const Counter = (props: {
  count: number;
  itemIndex?: number;
  data?: CartItemType;
  changeCounterView: ChangeCounterFrom;
  setCount?: (num: number) => void;
  isDisabled?: boolean;
  subitemId?: number;
  setDisableDelete?: (isDisabled: boolean) => void;
}) => {
  const { editItem, removeItem } = useCartMutations();
  const addCustomerAnalytics = useCustomerAnalytics();

  const [itemCount, setItemCount] = useState(props.count);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const pendingCountRef = useRef<number | null>(null);
  const isUpdatingRef = useRef(false);

  // Synchronize internal state with props
  useEffect(() => {
    setItemCount(props.count);
  }, [props.count]);

  // Handle the actual state update with proper debouncing
  const handleCountChange = (newCount: number) => {
    // Set the UI state immediately
    setItemCount(newCount);

    // Set the delete button to disabled state
    if (props.setDisableDelete) {
      props.setDisableDelete(true);
    }

    // Store the latest value that needs to be processed
    pendingCountRef.current = newCount;

    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Use immediate execution for zero (removal) operations
    const delay = newCount === 0 ? 0 : 500;

    // Set a new timer
    timerRef.current = setTimeout(() => {
      // Only proceed if there's a pending change
      if (pendingCountRef.current !== null) {
        const finalCount = pendingCountRef.current;
        pendingCountRef.current = null;

        // Make sure we don't have concurrent updates
        if (!isUpdatingRef.current) {
          isUpdatingRef.current = true;
          try {
            // Execute the action based on the final count
            executeCounterAction(finalCount);
          } finally {
            // Always reset the updating flag when done
            isUpdatingRef.current = false;
          }
        }

        // Reset the disabled state of delete button
        if (props.setDisableDelete) {
          props.setDisableDelete(false);
        }
      }
    }, delay);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  // Execute the actual action based on the counter value
  const executeCounterAction = (count: number) => {
    if (!props.data) {
      return;
    }

    const lineId = (props.data as { lineId?: string }).lineId;
    const canEditByLineId = !!lineId;
    const canEditByIndex =
      props.itemIndex !== undefined && props.itemIndex !== -1;

    if (!canEditByLineId && !canEditByIndex) {
      return;
    }

    if (count === 0) {
      if (lineId) {
        removeItem({ lineId });
      } else {
        removeItem({ itemIndex: props.itemIndex! });
      }

      return;
    }

    const editInput = {
      ...cartItemTypeToAddLineInput(props.data),
      count,
    };

    if (lineId) {
      editItem({ lineId, input: editInput });
    } else {
      editItem({ itemIndex: props.itemIndex!, input: editInput });
    }
  };

  const changeCount = (editType: EditCartItemEnum) => {
    if (props.changeCounterView === ChangeCounterFrom.ItemView) {
      // For ItemView, just pass changes up to parent
      if (editType === EditCartItemEnum.CounterIncChange) {
        if (props.setCount && props.count !== undefined) {
          props.setCount(props.count + 1);
        }
        addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.CHANGE_COUNTER, {
          state: "+",
          subitemId: props.subitemId,
          Screen: "ItemScreen",
        });
      } else {
        if (props.count && props.count > 1) {
          props.setCount && props.setCount(props.count - 1);
          addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.CHANGE_COUNTER, {
            state: "-",
            subitemId: props.subitemId,
            Screen: "ItemScreen",
          });
        }
      }
    } else if (props.data && props.itemIndex !== undefined) {
      // For cart view, handle with debouncing
      let newCount = itemCount;

      if (editType === EditCartItemEnum.CounterIncChange) {
        newCount = itemCount + 1;
      } else if (editType === EditCartItemEnum.CounterDecChange) {
        newCount = itemCount > 1 ? itemCount - 1 : 0;
      }

      handleCountChange(newCount);
    }
  };

  return (
    <div
      className={`${styles.box_shadow} ${
        props.isDisabled ? styles.counter_disabled : ""
      }`}
    >
      <div data-testid="counter-add-button">
        <div className={styles.add_number}>
          <button
            className={styles.icon_container}
            aria-label="plus"
            role="plus-button"
            data-testid={`plus-${props.data?.itemDetails.item_name}`}
            id={"plusbutton" + props.itemIndex}
            onClick={() => changeCount(EditCartItemEnum.CounterIncChange)}
            disabled={props.isDisabled}
          >
            <span className={styles.icon} data-testid="AddIcon">
              <AddIcon />
            </span>
          </button>
        </div>
      </div>
      <p className={styles.count_p} role="counterNumber">
        {localizedNumber(itemCount)}
      </p>
      <div data-testid="counter-remove-button">
        <div className={styles.remove_number}>
          <button
            className={styles.icon_container}
            aria-label="minus"
            data-testid={`minus-${props.data?.itemDetails.item_name}`}
            onClick={() => changeCount(EditCartItemEnum.CounterDecChange)}
            disabled={props.isDisabled}
          >
            <span className={styles.icon} data-testid="RemoveIcon">
              <MinusIcon />
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};
